<template>
  <div
    ref="invoiceMultiExportModal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="invoiceMultiExportModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5
            id="invoiceMultiExportModalLabel"
            class="modal-title"
          >
            <i class="fa fa-file-invoice-dollar me-2" />
            Export Invoices
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          />
        </div>

        <div class="modal-body">
          <div class="row g-3 mb-4">
            <div class="col-md-4">
              <label class="form-label">Issue Date</label>
              <input
                v-model="issueDate"
                type="date"
                class="form-control form-control-sm"
                :disabled="isExporting"
              >
            </div>
            <div class="col-md-4">
              <label class="form-label">Terms (days)</label>
              <input
                v-model.number="termsDays"
                v-no-wheel
                type="number"
                min="1"
                class="form-control form-control-sm"
                :disabled="isExporting"
              >
            </div>
            <div class="col-md-4">
              <label class="form-label">Due Date</label>
              <input
                :value="dueDate"
                type="date"
                class="form-control form-control-sm"
                disabled
              >
            </div>
            <div class="col-md-4">
              <label class="form-label">Invoice Number</label>
              <input
                v-model="invoiceNumber"
                type="text"
                class="form-control form-control-sm"
                :disabled="isFetchingNumber || isExporting"
                :placeholder="isFetchingNumber ? 'Loading…' : ''"
              >
            </div>
            <div class="col-md-4">
              <label class="form-label">
                Client Name <span class="text-danger">*</span>
              </label>
              <input
                v-model="clientName"
                type="text"
                class="form-control form-control-sm"
                :class="{ 'is-invalid': clientNameError }"
                :disabled="isExporting"
                @input="clientNameError = null"
              >
              <div
                v-if="clientNameError"
                class="invalid-feedback"
              >
                {{ clientNameError }}
              </div>
            </div>
            <div class="col-12">
              <label class="form-label">
                Client Address <span class="text-danger">*</span>
              </label>
              <textarea
                v-model="clientAddress"
                class="form-control form-control-sm"
                :class="{ 'is-invalid': addressError }"
                rows="2"
                :disabled="isExporting"
                @input="addressError = null"
              />
              <div
                v-if="addressError"
                class="invalid-feedback"
              >
                {{ addressError }}
              </div>
            </div>
          </div>

          <div
            v-for="section in sections"
            :key="section.projectId"
            class="project-export-section mb-4"
          >
            <div class="row g-2 mb-3">
              <div class="col-md-4">
                <label class="form-label">Project Code</label>
                <input
                  v-model="section.projectCode"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Optional"
                  :disabled="isExporting"
                >
              </div>
            </div>

            <div
              v-if="section.exportStatus"
              class="d-flex align-items-center justify-content-end flex-wrap gap-2 mb-3"
            >
              <span
                v-if="section.exportStatus === 'exporting'"
                class="badge bg-primary"
              >
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                />
                Exporting…
              </span>
              <span
                v-else-if="section.exportStatus === 'done'"
                class="badge bg-success"
              >
                <i class="fa fa-check me-1" /> Done
              </span>
              <span
                v-else-if="section.exportStatus === 'error'"
                class="badge bg-danger"
              >
                <i class="fa fa-times me-1" /> Error
              </span>
            </div>

            <div
              v-if="section.isLoading"
              class="text-center py-4"
            >
              <div
                class="spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div
              v-else-if="section.rows.length === 0"
              class="empty-state py-3 text-center text-muted"
            >
              <i class="fa fa-inbox me-2" />
              No completed payment requests for this project.
            </div>
            <div
              v-else
              class="table-responsive"
            >
              <table class="table table-sm table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th style="width: 36px;" />
                    <th>Expert</th>
                    <th>Email</th>
                    <th>Credits</th>
                    <th>Rate (USD)</th>
                    <th>Duration</th>
                    <th>Type</th>
                    <th class="text-end">
                      Amount (USD)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in section.rows"
                    :key="row.payment_request_id"
                  >
                    <td>
                      <input
                        v-model="section.selectedIds"
                        type="checkbox"
                        :value="row.payment_request_id"
                        :disabled="isExporting"
                      >
                    </td>
                    <td>{{ row.expert_name }}</td>
                    <td>{{ row.email }}</td>
                    <td>{{ row.credits }}</td>
                    <td>$ {{ formatAmount(row.rate) }}</td>
                    <td>{{ row.duration }} minutes</td>
                    <td>
                      <select
                        v-model="row.projectType"
                        class="form-select form-select-sm"
                        style="min-width: 130px;"
                        :disabled="isExporting"
                      >
                        <option value="">
                          - Select type -
                        </option>
                        <option
                          v-for="pref in section.engagementPreferences"
                          :key="pref.id"
                          :value="pref.name"
                        >
                          {{ pref.name }}
                        </option>
                      </select>
                    </td>
                    <td class="text-end">
                      <input
                        v-model.number="row.amount"
                        v-no-wheel
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control form-control-sm text-end"
                        style="width: 100px; display: inline-block;"
                        :disabled="isExporting"
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            :disabled="isExporting"
          >
            <i class="fa fa-times me-1" />
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="exportableSectionCount === 0 || isExporting"
            @click="onExportAll"
          >
            <i class="fa fa-file-pdf me-1" />
            <span v-if="isExporting">Exporting…</span>
            <span v-else>Export Invoice ({{ exportableSectionCount }} project(s))</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";

function createEmptySection(item) {
    return {
        projectId: item.projectId,
        clientName: item.clientName || "",
        clientAddress: "",
        projectCode: "",
        paymentRequestIds: item.paymentRequestIds || [],
        isLoading: true,
        invoiceNumber: "",
        engagementPreferences: [],
        rows: [],
        selectedIds: [],
        exportStatus: null,
    };
}

export default {
    name: "InvoiceMultiExportModal",
    props: {
        exportItems: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            modalInstance: null,
            sections: [],
            issueDate: "",
            termsDays: 30,
            clientName: "",
            clientNameError: null,
            clientAddress: "",
            addressError: null,
            isExporting: false,
            exportSucceeded: false,
            invoiceNumber: "",
            isFetchingNumber: false,
        };
    },
    computed: {
        dueDate() {
            if (!this.issueDate || !this.termsDays) return "";
            const d = new Date(this.issueDate);
            if (Number.isNaN(d.getTime())) return "";
            d.setDate(d.getDate() + parseInt(this.termsDays, 10));
            return d.toISOString().slice(0, 10);
        },
        exportableSectionCount() {
            return this.sections.filter((s) => s.selectedIds.length > 0).length;
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.modalInstance = new Modal(this.$refs.invoiceMultiExportModal);
            this._onHidden = () => this.onModalHidden();
            this.$refs.invoiceMultiExportModal.addEventListener("hidden.bs.modal", this._onHidden);
        });
    },
    beforeDestroy() {
        const modalElement = this.$refs.invoiceMultiExportModal;
        if (modalElement) {
            modalElement.removeEventListener("hidden.bs.modal", this._onHidden);
        }
    },
    methods: {
        show() {
            if (!this.modalInstance || !this.exportItems.length) {
                return;
            }

            this.exportSucceeded = false;
            this.issueDate = new Date().toISOString().slice(0, 10);
            this.termsDays = 30;
            this.sections = this.exportItems.map(createEmptySection);
            this.clientName = this.getSharedClientName();
            this.modalInstance.show();
            this.fetchAllSections();
            this.fetchInvoiceNumber();
        },
        hide() {
            if (this.modalInstance) {
                this.modalInstance.hide();
            }
        },
        onModalHidden() {
            if (this.exportSucceeded) {
                this.$emit("export-success");
            } else {
                this.$emit("export-cancelled");
            }
            this.reset();
        },
        reset() {
            this.sections = [];
            this.isExporting = false;
            this.exportSucceeded = false;
            this.issueDate = "";
            this.termsDays = 30;
            this.clientName = "";
            this.clientNameError = null;
            this.clientAddress = "";
            this.addressError = null;
            this.invoiceNumber = "";
            this.isFetchingNumber = false;
        },
        formatAmount(value) {
            return Number(value || 0).toFixed(2);
        },
        getSharedClientName() {
            const names = this.exportItems
                .map((item) => (item.clientName || "").trim())
                .filter((name) => name !== "");
            const uniqueNames = [...new Set(names)];

            return uniqueNames.length === 1 ? uniqueNames[0] : "";
        },
        getSharedClientAddress() {
            const addresses = this.sections
                .map((s) => (s.clientAddress || "").trim())
                .filter((addr) => addr !== "");
            const uniqueAddresses = [...new Set(addresses)];
            return uniqueAddresses.length === 1 ? uniqueAddresses[0] : "";
        },
        fetchInvoiceNumber() {
            if (!this.sections.length) return;
            this.isFetchingNumber = true;
            axios.get(`/admin/project/${this.sections[0].projectId}/invoice/next-number`)
                .then((response) => {
                    this.invoiceNumber = response?.data?.data?.invoice_number || "";
                })
                .catch(() => { this.invoiceNumber = ""; })
                .finally(() => { this.isFetchingNumber = false; });
        },
        fetchAllSections() {
            return Promise.all(this.sections.map((_, index) => this.fetchSection(index)))
                .then(() => {
                    if (!this.clientAddress) {
                        this.clientAddress = this.getSharedClientAddress();
                    }
                });
        },
        fetchSection(index) {
            const section = this.sections[index];
            if (!section || section.projectId == null || section.projectId === "") {
                return Promise.resolve();
            }

            section.isLoading = true;
            return axios
                .get(`/admin/project/${section.projectId}/invoice/eligible`)
                .then((response) => {
                    const defaultProjectType = response?.data?.meta?.project_type_name || "";
                    section.rows = (response?.data?.data || []).map((row) => ({
                        ...row,
                        projectType: defaultProjectType,
                    }));
                    section.clientAddress = response?.data?.meta?.client_address || "";
                    section.engagementPreferences = response?.data?.meta?.engagement_preferences || [];
                    const availableIds = section.rows.map((row) => row.payment_request_id);
                    const preselected = new Set(section.paymentRequestIds.map(Number));
                    section.selectedIds = availableIds.filter((id) => preselected.has(Number(id)));
                })
                .catch(() => {
                    section.rows = [];
                    section.engagementPreferences = [];
                    if (typeof One !== "undefined") {
                        One.helpers("jq-notify", {
                            type: "danger",
                            icon: "fa fa-times me-1",
                            message: `Failed to load payment requests for project #${section.projectId}.`,
                        });
                    }
                })
                .finally(() => {
                    section.isLoading = false;
                });
        },
        async onExportAll() {
            const toExport = this.sections.filter((s) => s.selectedIds.length > 0);
            if (toExport.length === 0) {
                return;
            }

            let hasError = false;
            if (!this.clientName.trim()) {
                this.clientNameError = "Client name is required.";
                hasError = true;
            }
            if (!this.clientAddress.trim()) {
                this.addressError = "Client address is required.";
                hasError = true;
            }
            if (hasError) {
                return;
            }

            this.isExporting = true;
            toExport.forEach((s) => { s.exportStatus = "exporting"; });

            try {
                const sections = toExport.map((s) => {
                    const amounts = {};
                    const types = {};
                    s.selectedIds.forEach((id) => {
                        const row = s.rows.find((r) => r.payment_request_id === id);
                        if (row) {
                            amounts[id] = row.amount;
                            types[id] = row.projectType;
                        }
                    });
                    return {
                        project_id: s.projectId,
                        project_code: s.projectCode,
                        payment_request_ids: s.selectedIds,
                        types,
                        amounts,
                    };
                });

                const response = await axios.post(
                    "/admin/invoice/multi-export",
                    {
                        invoice_number: this.invoiceNumber,
                        issue_date: this.issueDate,
                        terms_days: this.termsDays,
                        client_name: this.clientName.trim(),
                        client_address: this.clientAddress,
                        sections,
                    },
                    { responseType: "blob" }
                );

                const disposition = response.headers["content-disposition"] || "";
                const filenameMatch = /filename="?([^"]+)"?/.exec(disposition);
                const filename = filenameMatch ? filenameMatch[1] : "invoices.pdf";

                const blob = new Blob([response.data], { type: "application/pdf" });
                const fileUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = fileUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(fileUrl);

                toExport.forEach((s) => { s.exportStatus = "done"; });

                if (typeof One !== "undefined") {
                    One.helpers("jq-notify", {
                        type: "success",
                        icon: "fa fa-check me-1",
                        message: `${filename.replace(/\.pdf$/, "")} generated.`,
                    });
                }

                this.exportSucceeded = true;
                this.hide();
            } catch {
                toExport.forEach((s) => { s.exportStatus = "error"; });
                if (typeof One !== "undefined") {
                    One.helpers("jq-notify", {
                        type: "danger",
                        icon: "fa fa-times me-1",
                        message: "Multi-export failed.",
                    });
                }
            } finally {
                this.isExporting = false;
            }
        },
    },
};
</script>

<style scoped>
.project-export-section {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1rem 1.25rem;
}

.empty-state {
    font-size: .9rem;
}
</style>
